import { useCallback, useMemo, type JSX } from "react";

import type { I18n } from "@lingui/core";
import { I18nProvider } from "@lingui/react";

import {
  changeLocale,
  ENV_LOCALES,
  getBootFallbackFrom,
  LanguageContext,
  type LanguageContextType,
} from "./i18n";
import languagesMap from "./languages-map";
import { ReactUILocales } from "./react-ui";

type LocalizedProps = {
  i18n: I18n;
  children: React.ReactNode;
};

export function Localized({ children, i18n }: LocalizedProps): JSX.Element {
  const handleChangeLanguage = useCallback<
    LanguageContextType["onLanguageChange"]
  >(async (newLanguage) => {
    await changeLocale(newLanguage);
  }, []);

  const value = useMemo<LanguageContextType>(() => {
    return {
      language: i18n.locale,
      fallbackFrom: getBootFallbackFrom(),
      languagesMap: Object.fromEntries(languagesMap),
      availableLanguages: ENV_LOCALES,
      onLanguageChange: handleChangeLanguage,
    };
  }, [handleChangeLanguage, i18n.locale]);

  return (
    <LanguageContext value={value}>
      <I18nProvider i18n={i18n}>
        <ReactUILocales>{children}</ReactUILocales>
      </I18nProvider>
    </LanguageContext>
  );
}
